<template>
  <div class="payment-by-date-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Payment By Date"
        :start="start"
        :end="end"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Payment Details</div>
              <div class="report-subtitle">
                {{ Payment.length }} payment groups found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Total</div>
              <div class="summary-value">{{ total | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <div v-else>
              <div v-if="!Payment.length" class="empty-state">
                No payments found for this date range.
              </div>

              <div
                v-for="(payment, index) in Payment"
                :key="payment.id || index"
                class="payment-group"
              >
                <div class="payment-group-header">
                  <div>
                    <div class="payment-date">
                      {{ payment.date | moment("MM/DD/YYYY") }}
                    </div>
                    <div class="payment-method">
                      {{ payment.paymentMethod || "N/A" }}
                    </div>
                  </div>

                  <div class="payment-total">
                    {{ payment.total | currency }}
                  </div>
                </div>

                <PaymentDetail
                  v-if="payment.PaymentDetails && payment.PaymentDetails.length"
                  :PaymentId="payment.id"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import paymentService from "@/modules/Customer/Payment/service";

const PaymentDetail = () =>
  import("@/modules/ReportView/GlobalReport/PaymentDetail.vue");

export default {
  name: "payment-by-date-report",

  components: {
    ReportViewBar,
    PaymentDetail,
  },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Payment: [],
      exportFields: {
        Date: "date",
        Time: "time",
        "Payment Method": "paymentMethod",
        Customer: "customerName",
        Invoices: "invoices",
        Description: "description",
        "Payment Total": "amount",
      },
    };
  },

  computed: {
    total() {
      return this.Payment.reduce(
        (sum, row) => sum + Number(row.total || 0),
        0
      );
    },
    exportData() {
      return this.Payment.map((payment) => {
        const details = payment.PaymentDetails || [];
        const customerNames = details
          .map((detail) =>
            detail.Invoice && detail.Invoice.Customer
              ? detail.Invoice.Customer.name
              : null
          )
          .filter(Boolean);
        const invoices = details
          .map((detail) =>
            detail.Invoice && detail.Invoice.localId
              ? detail.Invoice.localId
              : null
          )
          .filter(Boolean);

        return {
          date: this.$moment(payment.date).format("MM/DD/YYYY"),
          time: this.$moment(payment.date).format("hh:mm A"),
          paymentMethod: payment.paymentMethod || "",
          customerName: [...new Set(customerNames)].join(", "),
          invoices: [...new Set(invoices)].join(", "),
          description: payment.note || "",
          amount: Number(payment.total || 0),
        };
      });
    },
  },

  async created() {
    this.start = this.$route.query.start || null;
    this.end = this.$route.query.end || null;

    await this.getAll();
  },

  methods: {
    async getAll() {
      try {
        const pageSize = 500;
        let offset = 0;
        let totalCount = 0;
        const payments = [];

        // The shared payment endpoint is intentionally paginated. Reports need
        // the complete selected range, so retrieve every page instead of only
        // displaying the endpoint's default first 50 records.
        do {
          const response = await paymentService.getAll({
            start: this.start,
            end: this.end,
            limit: pageSize,
            offset,
          });
          const rows = response.data || [];

          payments.push(...rows);
          totalCount = Number(response.count || rows.length);
          offset += rows.length;

          if (!rows.length) break;
        } while (offset < totalCount);

        this.Payment = payments;

        this.Payment.forEach((payment) => {
          payment.total = 0;

          (payment.PaymentDetails || []).forEach((detail) => {
            payment.total += Number(detail.amount || 0);
          });
        });
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-by-date-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.payment-group {
  border-bottom: 1px solid #e5e7eb;
}

.payment-group:last-child {
  border-bottom: none;
}

.payment-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f9fafb;
}

.payment-date {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.payment-method {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
}

.payment-total {
  font-size: 16px;
  font-weight: 800;
  color: var(--v-primary-base);
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

@media print {
  .payment-by-date-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>
